import { z } from "zod"
import { MediaFile } from "../../files/Models/MediaFile.js"
import { PoapSeo } from "./PoapSeo"
import { PoapCodeList } from "./PoapCodeList"
import { MediaFileType, type PoapType, PoapSchema, PoapStatusEnum, type PoapStatusType } from "@tutellus/schemas"

export const PoapValidation = z.object({
  id: z.string({ required_error: "_id is required" }),
  hashId: z.string({ required_error: "hashId is required" }),
  name: z.string({ required_error: "name is required" }),
  description: z.string({ required_error: "description is required" }),
  status: z.nativeEnum(PoapStatusEnum, { required_error: "status is required" }),
  eventDate: z.date({ required_error: "eventDate is required" }),
  perpetual: z.boolean({ required_error: "perpetual is required" }),
  energy: z.number({ required_error: "energy is required" }),
  ido: z.string().optional(),
  createdAt: z.date({ required_error: "createdAt is required" }),
  modifiedAt: z.date({ required_error: "modifiedAt is required" }),
  image: z.instanceof(MediaFile).optional(),
  video: z.instanceof(MediaFile).optional(),
  codes: z.instanceof(PoapCodeList).optional(),
  howto: z.string().optional(),
  seo: z.instanceof(PoapSeo),
  tokenId: z.number().optional(),
})

export class Poap {
  constructor(
    private readonly _id: string,
    private readonly _hashId: string,
    private readonly _name: string,
    private readonly _description: string,
    private readonly _status: PoapStatusType,
    private readonly _eventDate: Date,
    private readonly _perpetual: boolean,
    private readonly _energy: number,
    private readonly _createdAt: Date,
    private readonly _modifiedAt: Date,
    private readonly _seo: PoapSeo,
    private readonly _ido?: string,
    private readonly _image?: MediaFile,
    private readonly _video?: MediaFile,
    private readonly _codes?: PoapCodeList,
    private readonly _howto?: string,
    private readonly _tokenId?: number,
  ) {}

  static create({
    id,
    hashId,
    name,
    description,
    status,
    eventDate,
    perpetual,
    energy,
    ido,
    createdAt,
    modifiedAt,
    image,
    video,
    codes,
    howto,
    seo,
    tokenId,
  }: z.infer<typeof PoapValidation>) {
    const { error, success } = PoapValidation.safeParse({
      id,
      hashId,
      name,
      description,
      status,
      eventDate,
      perpetual,
      energy,
      ido,
      createdAt,
      modifiedAt,
      image,
      video,
      codes,
      howto,
      seo,
      tokenId,
    })
    if (!success) throw new Error(error.message)
    if (!image && !video) throw new Error("image or video is required")

    return new Poap(
      id,
      hashId,
      name,
      description,
      status,
      eventDate,
      perpetual,
      energy,
      createdAt,
      modifiedAt,
      seo,
      ido,
      image,
      video,
      codes,
      howto,
      tokenId,
    )
  }

  static createFromAPI(data: PoapType) {
    const { error, success } = PoapSchema.safeParse(data)
    if (!success) throw new Error(`Invalid Poap data: ${error.message}`)

    return Poap.create({
      id: data._id,
      hashId: data.hashId,
      name: data.name,
      description: data.description,
      status: data.status,
      eventDate: new Date(data.eventDate),
      perpetual: data.perpetual,
      energy: data.energy,
      ido: data.ido ?? undefined,
      createdAt: new Date(data.createdAt),
      modifiedAt: new Date(data.modifiedAt),
      image: data.image ? MediaFile.createFromAPI(data.image) : undefined,
      video: data.video ? MediaFile.createFromAPI(data.video) : undefined,
      codes: data.codes ? PoapCodeList.createFromAPI(data.codes) : undefined,
      howto: data.howto ?? undefined,
      seo: data.seo
        ? PoapSeo.createFromAPI(data.seo)
        : PoapSeo.create({ title: undefined, description: undefined, image: undefined }),
      tokenId: data.tokenId ?? undefined,
    })
  }

  get id() { return this._id } // prettier-ignore
  get hashId() { return this._hashId } // prettier-ignore
  get name() { return this._name } // prettier-ignore
  get description() { return this._description } // prettier-ignore
  get status() { return this._status } // prettier-ignore
  get eventDate() { return this._eventDate } // prettier-ignore
  get perpetual() { return this._perpetual } // prettier-ignore
  get energy() { return this._energy } // prettier-ignore
  get ido() { return this._ido } // prettier-ignore
  get createdAt() { return this._createdAt } // prettier-ignore
  get modifiedAt() { return this._modifiedAt } // prettier-ignore
  get image() { return this._image } // prettier-ignore
  get video() { return this._video } // prettier-ignore
  get codes() { return this._codes } // prettier-ignore
  get howto() { return this._howto } // prettier-ignore
  get seo() { return this._seo } // prettier-ignore
  get tokenId() { return this._tokenId } // prettier-ignore

  get isVideo() { return this.video?.url && this.video?.type === MediaFileType.VIDEO_MP4 } // prettier-ignore

  serialize() {
    return {
      id: this.id,
      hashId: this.hashId,
      name: this.name,
      description: this.description,
      status: this.status,
      eventDate: this.eventDate,
      perpetual: this.perpetual,
      energy: this.energy,
      ido: this.ido,
      createdAt: this.createdAt,
      modifiedAt: this.modifiedAt,
      image: this.image?.serialize(),
      video: this.video?.serialize(),
      codes: this.codes?.serialize(),
      howto: this.howto,
      seo: this.seo.serialize(),
      tokenId: this.tokenId,
    }
  }
}

export type PoapSerialized = ReturnType<Poap["serialize"]>
